/**
 * Emerald Books — Leave Management (requests, approvals, balances).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, CheckCircle2, XCircle, Ban, X, Calendar as CalIcon,
  Palmtree,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg, unwrapList } from './api';

const STATUS_CLS = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-rose-50 text-rose-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

/* ========================================================================= */
/* Requests                                                                    */
/* ========================================================================= */
export const LeaveRequestsPage = () => {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    employee_id: '', leave_type_id: '', start_date: '', end_date: '',
    half_day: false, reason: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, t, e] = await Promise.all([
        booksClient.get('/leave/requests', { params: filter === 'all' ? {} : { status: filter } }),
        booksClient.get('/leave/types'),
        booksClient.get('/hr/employees', { params: { status: 'active' } }),
      ]);
      setItems(unwrapList(r));
      setTypes(unwrapList(t));
      setEmployees(unwrapList(e));
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const submit = async (ev) => {
    ev.preventDefault();
    try {
      await booksClient.post('/leave/requests', form);
      toast.success('Leave requested — approval task created');
      setShowForm(false);
      setForm({ employee_id: '', leave_type_id: '', start_date: '', end_date: '', half_day: false, reason: '' });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async (id) => {
    try { await booksClient.post(`/leave/requests/${id}/approve`); toast.success('Approved'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason?', '');
    if (reason === null) return;
    try { await booksClient.post(`/leave/requests/${id}/reject`, { reason }); toast.success('Rejected'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try { await booksClient.post(`/leave/requests/${id}/cancel`); toast.success('Cancelled'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const seedDefaults = async () => {
    try {
      const r = await booksClient.post('/leave/seed-defaults');
      toast.success(`Seeded ${r.data.added} leave types`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Leave Requests</h1>
          <p className="text-sm text-slate-500">Request · approve · track</p>
        </div>
        <div className="flex gap-2">
          {types.length === 0 && (
            <Button variant="outline" onClick={seedDefaults} className="text-xs">
              <Palmtree className="w-3.5 h-3.5 mr-1" /> Seed defaults
            </Button>
          )}
          <Button onClick={() => setShowForm(true)} data-testid="leave-new-btn" className="bg-[#0f5c3d] hover:bg-[#0d4d33]">
            <Plus className="w-4 h-4 mr-1" /> Request leave
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            data-testid={`leave-filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${filter === f ? 'bg-[#0b1a2e] text-white' : 'bg-white text-slate-600 border'}`}>
            {f}
          </button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-800">New leave request</div>
            <button type="button" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></button>
          </div>
          <select required value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            className="w-full px-3 py-2 border rounded text-sm" data-testid="leave-emp-select">
            <option value="">Select employee…</option>
            {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name} · {e.employee_code}</option>)}
          </select>
          <select required value={form.leave_type_id} onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })}
            className="w-full px-3 py-2 border rounded text-sm" data-testid="leave-type-select">
            <option value="">Select leave type…</option>
            {types.map((t) => <option key={t.leave_type_id} value={t.leave_type_id}>{t.name} ({t.code}){t.country_code ? ` · ${t.country_code}` : ''}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            <Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} /> Half day
          </label>
          <textarea rows={2} className="w-full px-3 py-2 border rounded text-sm" placeholder="Reason (optional)"
            value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Button type="submit" data-testid="leave-submit-btn" className="w-full bg-[#0f5c3d] hover:bg-[#0d4d33]">Submit</Button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="space-y-2" data-testid="leave-list">
          {items.length === 0 && <div className="text-center text-slate-400 py-12">No leave requests</div>}
          {items.map((r) => (
            <div key={r.request_id} className="bg-white rounded-lg border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLS[r.status]}`}>{r.status}</span>
                    <span className="text-xs font-medium text-slate-700">{r.leave_type_name}</span>
                    <span className="text-xs text-slate-500">{r.days}d</span>
                  </div>
                  <div className="mt-1 font-semibold text-slate-800">{r.employee_name}</div>
                  <div className="text-xs text-slate-500">
                    {r.start_date} → {r.end_date}{r.half_day ? ' · half day' : ''}
                  </div>
                  {r.reason && <div className="text-xs text-slate-500 mt-1">{r.reason}</div>}
                  {r.reject_reason && <div className="text-xs text-rose-600 mt-1">Rejected: {r.reject_reason}</div>}
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-1">
                    <button onClick={() => approve(r.request_id)} className="p-2 rounded hover:bg-emerald-50 text-emerald-600" title="Approve" data-testid={`leave-approve-${r.request_id}`}>
                      <CheckCircle2 className="w-5 h-5" />
                    </button>
                    <button onClick={() => reject(r.request_id)} className="p-2 rounded hover:bg-rose-50 text-rose-600" title="Reject" data-testid={`leave-reject-${r.request_id}`}>
                      <XCircle className="w-5 h-5" />
                    </button>
                    <button onClick={() => cancel(r.request_id)} className="p-2 rounded hover:bg-slate-50 text-slate-500" title="Cancel">
                      <Ban className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========================================================================= */
/* Balance                                                                     */
/* ========================================================================= */
export const LeaveBalancePage = () => {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState('');
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await booksClient.get('/hr/employees', { params: { status: 'active' } });
        setEmployees(unwrapList(r));
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, []);

  const load = async (id) => {
    setSelected(id); if (!id) { setBalance(null); return; }
    setLoading(true);
    try {
      const r = await booksClient.get('/leave/balance', { params: { employee_id: id } });
      setBalance(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-[#0b1a2e]">Leave Balance</h1>
        <p className="text-sm text-slate-500">Entitlement · taken · pending · remaining</p>
      </div>
      <select value={selected} onChange={(e) => load(e.target.value)}
        className="w-full max-w-md px-3 py-2 border rounded text-sm bg-white">
        <option value="">Select employee…</option>
        {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name} · {e.employee_code}</option>)}
      </select>
      {loading && <Loader2 className="animate-spin text-slate-400" />}
      {balance && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="leave-balance-grid">
          {balance.balances.map((b) => (
            <div key={b.leave_type_id} className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 rounded" style={{ background: b.color || '#0f5c3d' }} />
                <div>
                  <div className="font-semibold text-slate-800">{b.leave_type_name}</div>
                  <div className="text-xs text-slate-500">{b.leave_type_code} · {b.accrual_type}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div><div className="text-xs text-slate-500">Entitled</div><div className="font-semibold">{b.entitlement}</div></div>
                <div><div className="text-xs text-slate-500">Taken</div><div className="font-semibold text-rose-600">{b.taken}</div></div>
                <div><div className="text-xs text-slate-500">Balance</div><div className="font-semibold text-emerald-600">{b.balance}</div></div>
              </div>
              {b.pending > 0 && <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">Pending approval: {b.pending}d</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========================================================================= */
/* Types                                                                       */
/* ========================================================================= */
export const LeaveTypesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: '', name: '', country_code: '', accrual_type: 'fixed_annual',
    annual_days: 0, paid: true, color: '#0f5c3d',
  });
  const load = async () => {
    setLoading(true);
    try { const r = await booksClient.get('/leave/types'); setItems(unwrapList(r)); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, annual_days: Number(form.annual_days || 0) };
      if (!payload.country_code) delete payload.country_code;
      await booksClient.post('/leave/types', payload);
      toast.success('Leave type created');
      setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#0b1a2e]">Leave Types</h1>
        <Button onClick={() => setShowForm(true)} className="bg-[#0f5c3d] hover:bg-[#0d4d33]"><Plus className="w-4 h-4 mr-1" /> New</Button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border grid sm:grid-cols-2 gap-2">
          <Input placeholder="Code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input placeholder="Country code (blank = global)" value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} />
          <select value={form.accrual_type} onChange={(e) => setForm({ ...form, accrual_type: e.target.value })} className="px-3 py-2 border rounded text-sm">
            <option value="fixed_annual">Fixed annual</option>
            <option value="monthly_accrue">Monthly accrue</option>
            <option value="unlimited">Unlimited</option>
          </select>
          <Input type="number" step="0.5" placeholder="Annual days" value={form.annual_days} onChange={(e) => setForm({ ...form, annual_days: e.target.value })} />
          <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          <Button type="submit" className="sm:col-span-2 bg-[#0f5c3d] hover:bg-[#0d4d33]">Save</Button>
        </form>
      )}
      {loading ? <Loader2 className="animate-spin" /> : (
        <div className="grid gap-2 sm:grid-cols-2">
          {items.map((t) => (
            <div key={t.leave_type_id} className="bg-white rounded-lg border p-3 flex items-center gap-3">
              <div className="w-2 h-8 rounded" style={{ background: t.color || '#0f5c3d' }} />
              <div className="flex-1">
                <div className="font-semibold text-slate-800">{t.name}</div>
                <div className="text-xs text-slate-500">{t.code} · {t.country_code || 'global'} · {t.annual_days}d/yr · {t.accrual_type}</div>
              </div>
              {t.paid ? <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">Paid</span> :
                <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Unpaid</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
